// ISA atmosphere and the unit constants every other range module converts with.
// Keep conversions here so that no data file carries a hand-converted number without its source value.

/** metres per foot */
export const FT = 0.3048;
/** metres per nautical mile */
export const NM_M = 1852;
/** m/s per knot */
export const KT_MS = 0.514444;
/** standard gravity, m/s² */
export const G = 9.80665;
/** kg per pound (mass) */
export const LB_KG = 0.45359237;
/** kW per mechanical horsepower */
export const HP_KW = 0.7457;
/** litres per US gallon */
export const GAL_L = 3.78541;
/** newtons per pound-force */
export const LBF_N = 4.4482216;

/** Avgas 0.72 kg/L (6.01 lb/US gal at 15 °C) — Wikipedia: Avgas (research notes, unit block). */
export const AVGAS_KG_PER_L = 0.72;
/** Jet A-1 0.804 kg/L — Wikipedia: Jet fuel; rounded to the plan's 0.8. */
export const JETA_KG_PER_L = 0.8;
/** Marine diesel 0.84 kg/L — the basis Volvo Penta states for its fuel charts. */
export const DIESEL_KG_PER_L = 0.84;
/** estimate: automotive gasoline ~0.74 kg/L (ship notes, Q2 code block, labelled estimate). */
export const GASOLINE_KG_PER_L = 0.74;
/** estimate: heavy fuel oil ~0.98 kg/L (ship notes, Q2 code block, labelled estimate). */
export const HFO_KG_PER_L = 0.98;

/** ISA sea-level density, kg/m³ */
export const RHO0 = 1.225;
const T0 = 288.15;
const L = 0.0065;
const R = 287.05;
const GAMMA = 1.4;
const T11 = T0 - L * 11000;
const RHO11 = RHO0 * Math.pow(T11 / T0, G / (L * R) - 1);

/** ISA temperature (K), troposphere + lower stratosphere (to 20 km). */
export function isaTemperatureK(altM: number): number {
  const h = Math.max(0, Math.min(altM, 20000));
  return h <= 11000 ? T0 - L * h : T11;
}

/** ISA density (kg/m³), troposphere + lower stratosphere (to 20 km). */
export function isaDensity(altM: number): number {
  const h = Math.max(0, Math.min(altM, 20000));
  if (h <= 11000) {
    const T = T0 - L * h;
    return RHO0 * Math.pow(T / T0, G / (L * R) - 1);
  }
  return RHO11 * Math.exp((-G * (h - 11000)) / (R * T11));
}

/** σ = ρ/ρ0 */
export function densityRatio(altM: number): number {
  return isaDensity(altM) / RHO0;
}

/** Speed of sound (m/s) in ISA. */
export function speedOfSound(altM: number): number {
  return Math.sqrt(GAMMA * R * isaTemperatureK(altM));
}

// ---------------------------------------------------------------- conversions used by the data files

export const ftToM = (ft: number): number => ft * FT;
export const mToFt = (m: number): number => m / FT;
export const ktToMs = (kt: number): number => kt * KT_MS;
export const msToKt = (ms: number): number => ms / KT_MS;
export const lbToKg = (lb: number): number => lb * LB_KG;
export const hpToKw = (hp: number): number => hp * HP_KW;
export const lbfToN = (lbf: number): number => lbf * LBF_N;
/** Fuel mass from a volume in US gallons and a density in kg/L. */
export const galToKg = (gal: number, kgPerL: number): number => gal * GAL_L * kgPerL;
/** Brake specific fuel consumption lb/(hp·h) → kg/kWh. */
export const bsfcFromLbPerHpH = (v: number): number => (v * LB_KG) / HP_KW;
/** Thrust specific fuel consumption lb/(lbf·h) → kg/(N·h). */
export const tsfcFromLbPerLbfH = (v: number): number => (v * LB_KG) / LBF_N;
/** g/kWh → kg/kWh */
export const gPerKwhToKg = (v: number): number => v / 1000;
